import { useEffect, useMemo, useRef, useState, type PointerEvent } from 'react'
import { machineFiles } from '@/content/system'
import WavingFlag from '../marks/WavingFlag'
import AgencyMark from '../marks/AgencyMark'
import { america } from '@/content/missions'
import { useReducedMotion } from '@/state/useReducedMotion'

// The visuals beside each step of the agents section. Each is a different
// kind of motion; all of them hold a single still frame under reduced motion.

/** The real manifest, typed out line by line with a blinking cursor. */
export function ManifestStream({ active, still }: { active: boolean; still: boolean }) {
  const lines = useMemo(() => machineFiles()['manifest.json'].split('\n').slice(0, 26), [])
  const total = lines.join('\n').length
  const [typed, setTyped] = useState(0)

  useEffect(() => {
    if (still || !active) return
    const start = performance.now()
    const timer = window.setInterval(() => {
      const count = Math.min(total, Math.floor(((performance.now() - start) / 16) * 9))
      setTyped(count)
      if (count >= total) window.clearInterval(timer)
    }, 16)
    return () => window.clearInterval(timer)
  }, [active, still, total])

  const shown = still ? total : active ? typed : 0
  const text = lines.join('\n').slice(0, shown)
  const [matrix, setMatrix] = useState(false)
  return (
    <div className={`agent-scene agent-scene--manifest${matrix ? ' is-matrix' : ''}`} aria-hidden="true">
      <div className="agent-scene__window" onPointerEnter={() => setMatrix(true)} onPointerLeave={() => setMatrix(false)}>
        {matrix && !still && <MatrixRain glyphs={lines.join('')} />}
        <div className="agent-scene__bar">
          <span />
          <span />
          <span />
          <code>/mds/manifest.json</code>
        </div>
        <pre>
          <code>
            {text.split('\n').map((line, i) => (
              <span key={i} className="agent-scene__line">
                {line.replace(/"([^"]+)":/g, '​"$1":')}
                {'\n'}
              </span>
            ))}
            <span className="agent-scene__cursor" />
          </code>
        </pre>
      </div>
    </div>
  )
}

/** Green glyphs falling down the window while the pointer rests on it. */
function MatrixRain({ glyphs }: { glyphs: string }) {
  const ref = useRef<HTMLCanvasElement>(null)
  useEffect(() => {
    const canvas = ref.current
    const ctx = canvas?.getContext('2d')
    if (!canvas || !ctx) return
    const size = 14
    const scale = Math.min(window.devicePixelRatio || 1, 2)
    canvas.width = canvas.clientWidth * scale
    canvas.height = canvas.clientHeight * scale
    ctx.setTransform(scale, 0, 0, scale, 0, 0)
    ctx.font = `${size}px "Geist Mono Variable", monospace`
    const drops = Array.from({ length: Math.ceil(canvas.clientWidth / size) }, () => Math.random() * -40)
    const chars = glyphs.replace(/\s/g, '')
    let frame = 0
    let last = 0
    const draw = (now: number) => {
      frame = requestAnimationFrame(draw)
      if (now - last < 50) return
      last = now
      // Fade what is there, then draw the next glyph at the head of each column.
      ctx.globalCompositeOperation = 'destination-out'
      ctx.fillStyle = 'rgba(0, 0, 0, 0.12)'
      ctx.fillRect(0, 0, canvas.clientWidth, canvas.clientHeight)
      ctx.globalCompositeOperation = 'source-over'
      drops.forEach((y, column) => {
        ctx.fillStyle = Math.random() < 0.08 ? '#d7ffd9' : '#21d34a'
        ctx.fillText(chars[Math.floor(Math.random() * chars.length)], column * size, y * size)
        drops[column] = y * size > canvas.clientHeight && Math.random() > 0.96 ? 0 : y + 1
      })
    }
    frame = requestAnimationFrame(draw)
    return () => cancelAnimationFrame(frame)
  }, [glyphs])
  return <canvas ref={ref} className="agent-scene__rain" />
}

/**
 * The flag in WebGL. It holds still until the pointer moves across it, then
 * waves and ripples under the pointer, settling again when the pointer stops.
 */
export function FlagScene({ active, still }: { active: boolean; still: boolean }) {
  const [moving, setMoving] = useState(false)
  const rest = useRef(0)
  const stir = () => {
    setMoving(true)
    window.clearTimeout(rest.current)
    rest.current = window.setTimeout(() => setMoving(false), 400)
  }
  useEffect(() => () => window.clearTimeout(rest.current), [])
  return (
    <div className="agent-scene agent-scene--flag" aria-hidden="true">
      <div
        className="agent-scene__flag"
        onPointerMove={stir}
        onPointerLeave={() => {
          window.clearTimeout(rest.current)
          setMoving(false)
        }}
      >
        {/* The WebGL context exists only while the step is showing, so the
            page never holds more live contexts than the browser allows. */}
        {active && !still ? (
          <WavingFlag
            paused={!moving}
            interactive
            flatWhenPaused
            fallback={<AgencyMark mission={america} variant="flag" className="agent-scene__still" />}
          />
        ) : (
          <AgencyMark mission={america} variant="flag" className="agent-scene__still" />
        )}
      </div>
    </div>
  )
}

/** A page assembling itself: header, hero, actions and cards snap into place. */
export function AssembleScene({ active }: { active: boolean }) {
  // Each block has a desktop place and a phone place; hovering the page
  // reflows it to the phone layout, the way the components do at 320px.
  const blocks = [
    { name: 'banner', x: 0, y: 0, w: 100, h: 4, phone: [0, 0, 100, 3] },
    { name: 'header', x: 0, y: 6, w: 100, h: 9, phone: [0, 5, 100, 7] },
    { name: 'hero', x: 0, y: 18, w: 100, h: 34, phone: [0, 14, 100, 30] },
    { name: 'title', x: 18, y: 25, w: 64, h: 9, phone: [10, 19, 80, 8] },
    { name: 'pill', x: 26, y: 38, w: 48, h: 7, phone: [12, 32, 76, 7] },
    { name: 'card', x: 0, y: 56, w: 31.5, h: 30, phone: [0, 47, 100, 13] },
    { name: 'card', x: 34.25, y: 56, w: 31.5, h: 30, phone: [0, 62, 100, 13] },
    { name: 'card', x: 68.5, y: 56, w: 31.5, h: 30, phone: [0, 77, 100, 13] },
    { name: 'footer', x: 0, y: 90, w: 100, h: 10, phone: [0, 92, 100, 8] },
  ]
  const [phone, setPhone] = useState(false)
  return (
    // The hover sits on the scene, which keeps its size, not on the page that
    // shrinks; otherwise the page slides out from under the pointer and back.
    <div
      className={`agent-scene agent-scene--assemble${active ? ' is-active' : ''}${phone ? ' is-phone' : ''}`}
      aria-hidden="true"
      onPointerEnter={() => setPhone(true)}
      onPointerLeave={() => setPhone(false)}
    >
      <div className="agent-scene__page">
        {blocks.map((block, i) => (
          <span
            key={i}
            className={`agent-block agent-block--${block.name}`}
            style={
              {
                '--x': `${block.x}%`,
                '--y': `${block.y}%`,
                '--w': `${block.w}%`,
                '--h': `${block.h}%`,
                '--px': `${block.phone[0]}%`,
                '--py': `${block.phone[1]}%`,
                '--pw': `${block.phone[2]}%`,
                '--ph': `${block.phone[3]}%`,
                '--from-x': `${(i % 3) * 40 - 40}px`,
                '--from-y': `${(i % 2 ? -1 : 1) * (60 + i * 8)}px`,
                '--delay': `${i * 90}ms`,
              } as React.CSSProperties
            }
          />
        ))}
      </div>
    </div>
  )
}

const SWEEP_MS = 2400
/** The sweep's leading edge sits 60 degrees clockwise of its start. */
const SWEEP_LEAD = 60

/**
 * A radar sweep, a check drawing itself and the checks passing one by one.
 * The cursor is a contact: it pings each time the sweep's leading edge
 * passes over it, once a rotation.
 */
export function VerifyScene({ active }: { active: boolean }) {
  const checks = ['Types', 'Lint', 'axe, every route', 'Keyboard paths', '320px reflow', 'Contrast, every theme']
  const reduced = useReducedMotion()
  const sweep = useRef<SVGGElement>(null)
  const contact = useRef<{ x: number; y: number } | null>(null)
  const [pings, setPings] = useState<Array<{ id: number; x: number; y: number }>>([])

  useEffect(() => {
    if (!active || reduced) return
    let frame = 0
    let previous = -1
    const start = performance.now()
    const turn = (now: number) => {
      frame = requestAnimationFrame(turn)
      const angle = (((now - start) / SWEEP_MS) * 360) % 360
      sweep.current?.setAttribute('transform', `rotate(${angle} 100 100)`)
      const lead = (angle + SWEEP_LEAD) % 360
      const point = contact.current
      if (point && previous >= 0) {
        const bearing = (Math.atan2(point.x - 100, 100 - point.y) * 180) / Math.PI
        const target = (bearing + 360) % 360
        const crossed = previous <= lead ? target > previous && target <= lead : target > previous || target <= lead
        if (crossed) {
          const id = now
          const { x, y } = point
          setPings((current) => [...current.slice(-3), { id, x, y }])
          window.setTimeout(() => setPings((current) => current.filter((item) => item.id !== id)), 1400)
        }
      }
      previous = lead
    }
    frame = requestAnimationFrame(turn)
    return () => cancelAnimationFrame(frame)
  }, [active, reduced])

  const track = (event: PointerEvent<SVGSVGElement>) => {
    const rect = event.currentTarget.getBoundingClientRect()
    const x = ((event.clientX - rect.left) / rect.width) * 200
    const y = ((event.clientY - rect.top) / rect.height) * 200
    contact.current = Math.hypot(x - 100, y - 100) <= 92 ? { x, y } : null
  }

  return (
    <div className={`agent-scene agent-scene--verify${active ? ' is-active' : ''}`} aria-hidden="true">
      <svg
        viewBox="0 0 200 200"
        className="agent-scene__radar"
        onPointerMove={track}
        onPointerLeave={() => {
          contact.current = null
        }}
      >
        {[90, 66, 42].map((r) => (
          <circle key={r} cx="100" cy="100" r={r} fill="none" stroke="var(--mds-border-strong)" strokeWidth="0.75" />
        ))}
        <g ref={sweep} className="agent-scene__sweep">
          <path d="M100 100 L100 10 A90 90 0 0 1 177.9 55 Z" fill="url(#sweep)" />
        </g>
        <defs>
          <linearGradient id="sweep" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0" stopColor="var(--mds-link)" stopOpacity="0" />
            <stop offset="1" stopColor="var(--mds-link)" stopOpacity="0.45" />
          </linearGradient>
        </defs>
        {pings.map((item) => (
          <g key={item.id} className="agent-scene__ping" transform={`translate(${item.x} ${item.y})`}>
            <circle r="9" fill="none" stroke="var(--mds-success)" strokeWidth="1.25" />
            <circle r="2.4" fill="var(--mds-success)" />
          </g>
        ))}
        <path
          className="agent-scene__check"
          d="M66 102 L90 126 L136 78"
          fill="none"
          stroke="var(--mds-success)"
          strokeWidth="10"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <ul className="agent-scene__checks">
        {checks.map((check, i) => (
          <li key={check} style={{ '--delay': `${400 + i * 220}ms` } as React.CSSProperties}>
            <span className="agent-scene__tick">✓</span>
            {check}
          </li>
        ))}
      </ul>
    </div>
  )
}
